import axios from 'axios';

import { apiClient } from '@/services/api/api';
import { logger } from '@/utils/logger/loggerInstance';

const apiUrl = import.meta.env.VITE_API_URL as string;

// Public client — no auth header injected (these are unauthenticated endpoints)
const publicApiClient = axios.create({
  baseURL: apiUrl,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

export interface InviteDetails {
  email: string;
  firstName?: string;
  lastName?: string;
  role: 'Buyer' | 'Advisor' | 'Admin';
  status: 'pending' | 'in_progress' | 'accepted' | 'expired';
  expired: boolean;
  accountExists: boolean;
  authProvider: 'google' | 'apple' | 'email' | null;
}

export interface CreateInviteRequest {
  email: string;
  firstName: string;
  lastName: string;
  role: 'Advisor' | 'Admin';
}

/**
 * Fetch invite metadata by token (public, no auth required).
 */
export const getInvite = async (token: string): Promise<InviteDetails> => {
  const url = `/invites/${encodeURIComponent(token)}`;
  logger.debug(`GET ${url}`);
  const response = await publicApiClient.get<InviteDetails>(url);
  return response.data;
};

/**
 * Accept an invite: provision a Cognito account with the given password (public, no auth required).
 */
export const acceptInvite = async (token: string, password: string): Promise<void> => {
  const url = `/invites/${encodeURIComponent(token)}/accept`;
  logger.debug(`POST ${url}`);
  await publicApiClient.post(url, { password });
};

/**
 * Create a new staff invite (admin only).
 */
export const createInvite = async (request: CreateInviteRequest): Promise<void> => {
  const url = '/invites';
  logger.debug(`POST ${url}`);
  await apiClient.post(url, request);
};

export const requestSelfSignup = async (email: string): Promise<{ success: true }> => {
  const url = '/invites/self-signup';
  logger.debug(`POST ${url}`);
  const response = await publicApiClient.post<{ success: true }>(url, { email });
  return response.data;
};

/**
 * Resend an invite email by member email address (advisor or admin only).
 * Looks up the pending invite on the server side.
 */
export const resendInviteByEmail = async (email: string): Promise<void> => {
  const url = '/invites/resend';
  logger.debug(`POST ${url}`);
  await apiClient.post(url, { email });
};

/**
 * Claim all pending invites for the currently authenticated user.
 * Called when an already-signed-in user lands on the accept-invite page.
 */
export const claimInvites = async (): Promise<{ claimed: number; failed: number }> => {
  const url = '/invites/claim';
  logger.debug(`POST ${url}`);
  const response = await apiClient.post<{ claimed: number; failed: number }>(url);
  return response.data;
};
